import React, { FC, useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import classNames from 'classnames'
import orderBy from 'lodash/orderBy'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { INoteableTypes, IShipmentIssueStates } from 'types/graphqlTypes'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { EdiDuplicateFacilitiesWarningMessage } from 'apps/marketplace/components/EdiDuplicateFacilities'
import Notes from 'apps/marketplace/components/Notes'
import { useIssuePriorityLabel } from 'apps/marketplace/pages/issues/shared/priorityOptions'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'
import {
  SelectSubscribers,
  SelectDueDate,
  SelectOwner,
  SelectStatus,
  TextSummary,
} from '../../CreateNewIssueModal/components/forms'
import { isVerifyAddressIssue } from '../../CreateNewIssueModal/components/forms/utils'
import { IShipmentIssueDetailsFragment } from '../graphql/ShipmentIssueDetailsFragment'
import { useShipmentIssuesOptionsQuery } from '../graphql/ShipmentIssuesOptions'
import useEditIssueForm from '../hooks/useEditIssueForm'
import { IssueStepsToResolve } from './issueStepsToResolve'
import { STATE_TRANSITION_DESCRIPTION } from './utils'

interface IIssueDetailsProps {
  issue: IShipmentIssueDetailsFragment
  shipmentId: string
  readOnly?: boolean
}

const useIssueDetailsStyles = makeStyles(theme => ({
  uneditableValue: {
    marginTop: theme.spacing(1),
  },
}))

const UUID_REGEX = /[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}/i

/**
 * Issue View / Edit page
 */
const IssueDetails: FC<IIssueDetailsProps> = ({ issue, readOnly = false, shipmentId }) => {
  const { data: options, refetch } = useShipmentIssuesOptionsQuery()

  const [isEditingStepsToResolve, setIsEditingStepsToResolve] = useState(false)
  const [showStepsToResolve, setShowStepsToResolve] = useState(true)

  const { editIssue, formProps } = useEditIssueForm(issue)

  const issueType = useMemo(() => options?.types.find(({ value }) => value === issue?.type.id), [
    options,
    issue?.type.id,
  ])

  const classes = useIssueDetailsStyles()
  const { className: priorityClassName, label: priorityLabel } = useIssuePriorityLabel(
    issue?.priority
  )

  const issueSLAsAndEscalationEnabled = useFeatureFlag('issueSLAsAndEscalation')
  const isMpStepsToResolveEnabled = useFeatureFlag('mpStepsToResolve')

  if (!issue) {
    return null
  }

  const [facilityUuid = ''] = issue.summary?.match(UUID_REGEX) || []
  const { issueStateTransitions = [] } = issue || {}
  const sortedIssueStateTransitions = orderBy(
    issueStateTransitions,
    [({ createdAt }) => new Date(createdAt)],
    ['desc']
  )

  return (
    <FormProvider {...formProps}>
      <Box
        display='flex'
        flexDirection='row'
        id={`issue-details-${issue.id}`}
        justifyContent='space-between'
        data-dd-action-name='Show Issue Details'>
        {/* Left: Main content body */}
        <Box padding={2} flex='3 600px'>
          <Typography variant='h3'>
            {issue.type.category?.name}
            {': '}
            {issue.type.name}{' '}
            <Box component='span' color={brandColors.coolGray5}>
              (Issue {shipmentId}-{issue?.id})
            </Box>
          </Typography>
          <Box component='span' color={brandColors.coolGray5}>
            {issueType?.description}
          </Box>
          <Box mt={4}>
            <TextSummary value={issue?.summary} readOnly={readOnly} onChange={editIssue} />
          </Box>
          {isVerifyAddressIssue(issue.type) && issue.state !== IShipmentIssueStates.resolved && (
            <div>
              <EdiDuplicateFacilitiesWarningMessage
                facilityUuid={facilityUuid}
                shipmentId={shipmentId}
                onSuccess={refetch}
              />
            </div>
          )}
          <Box mt={4}>
            <Typography variant='subtitle1'>Notes</Typography>
            <Box my={1}>
              <Typography variant='caption' color='textSecondary'>
                All notes will be automatically tagged with owners &amp; subscribers
              </Typography>
            </Box>
            <Notes
              dataTest='shipment-issue-notes'
              noteableId={shipmentId}
              noteableType={INoteableTypes.ShipmentIssue}
              issue={issue}
              maxHeight={40}
              disableHeader
              disableBorder
              disableFilters
              readOnly={readOnly}
            />
          </Box>
        </Box>
        {/* Right Rail */}
        <Box flex='2 400px' padding={2}>
          <SelectStatus issue={issue} onChange={editIssue} />
          <Box mt={4}>
            <SelectOwner
              value={issue?.owner}
              onChange={editIssue}
              readOnly={readOnly}
              resolutionSteps={issue.issueResolutionSteps || null}
              showStepsToResolve={!isEditingStepsToResolve}
              onEditChange={isEditMode =>
                setShowStepsToResolve(!(isEditMode && !isEditingStepsToResolve))
              }
            />
          </Box>
          {isMpStepsToResolveEnabled && showStepsToResolve && (
            <Box display='flex' mt={4} flexDirection='column'>
              <IssueStepsToResolve
                steps={issue.type.stepsToResolve || null}
                resolutionSteps={issue.issueResolutionSteps || null}
                onSubmit={editIssue}
                onEditChange={isEditMode => setIsEditingStepsToResolve(isEditMode)}
              />
            </Box>
          )}
          <Box display='flex' mt={4} flexDirection='column'>
            {issueSLAsAndEscalationEnabled ? (
              <>
                <Typography variant='subtitle1'>Due</Typography>
                <div className={classes.uneditableValue}>
                  {issue.dueAt ? (
                    <FormattedDateTime
                      inSentence
                      showTimezone
                      datetime={issue.dueAt}
                      format='T24TimeWeekdayDateAndYear'
                      displayTimezone={FORMATS.defaultTimeZone}
                    />
                  ) : (
                    DISPLAY.empty
                  )}
                </div>
              </>
            ) : (
              <SelectDueDate value={issue?.dueAt} onChange={editIssue} />
            )}
          </Box>
          {!isMpStepsToResolveEnabled && (
            <Box display='flex' mt={4} flexDirection='column'>
              <Typography variant='subtitle1'>Steps To Resolve</Typography>
              <IssueStepsToResolve
                steps={issue.type.stepsToResolve || null}
                resolutionSteps={issue.issueResolutionSteps || null}
              />
            </Box>
          )}
          <Box display='flex' mt={4} flexDirection='column'>
            <Typography variant='subtitle1'>Priority</Typography>
            <div
              className={classNames(classes.uneditableValue, {
                [priorityClassName]: !!priorityLabel,
              })}>
              {priorityLabel || DISPLAY.empty}
            </div>
          </Box>
          <Box display='flex' mt={4} flexDirection='column'>
            <Typography variant='subtitle1'>Subscribers</Typography>
            <Box mt={1}>
              <SelectSubscribers
                subscribers={issue.subscribers}
                readOnly={readOnly}
                onChange={editIssue}
              />
            </Box>
          </Box>
          <Box display='flex' mt={4} flexDirection='column'>
            <Typography variant='subtitle1'>Activity</Typography>
            {sortedIssueStateTransitions.map(({ createdAt, createdBy, id, state }) => (
              <Typography key={id} color='textSecondary'>
                <strong>{STATE_TRANSITION_DESCRIPTION[state]} </strong>
                {createdBy.fullName}{' '}
                {createdAt && (
                  <FormattedDateTime
                    datetime={createdAt}
                    format='T24TimeWeekdayDateAndYear'
                    displayTimezone={FORMATS.defaultTimeZone}
                  />
                )}
              </Typography>
            ))}
            <Typography color='textSecondary'>
              <strong>Created by </strong>
              {issue?.createdBy.firstName} {issue?.createdBy.lastName}{' '}
              {issue?.createdAt && (
                <FormattedDateTime
                  datetime={issue?.createdAt}
                  format='T24TimeWeekdayDateAndYear'
                  displayTimezone={FORMATS.defaultTimeZone}
                />
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  )
}

export default IssueDetails
